import { createTheme } from '@mui/material/styles';

/** Apple.com-style system font stack (SF Pro on Apple devices). */
export const APPLE_FONT_STACK = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"SF Pro Display"',
  '"SF Pro Text"',
  'system-ui',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Helvetica',
  'Arial',
  'sans-serif',
].join(', ');

const appleHeadline = {
  fontFamily: APPLE_FONT_STACK,
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: '#1d1d1f',
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0071e3',
      light: '#339cff',
      dark: '#005bb5',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1d1d1f',
      secondary: '#6e6e73',
    },
    error: {
      main: '#0071e3',
    },
    warning: {
      main: '#0071e3',
    },
    info: {
      main: '#000000',
      light: '#f5f5f7',
    },
    success: {
      main: '#000000',
    },
  },
  typography: {
    fontFamily: APPLE_FONT_STACK,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      ...appleHeadline,
      fontSize: '3.5rem',
      lineHeight: 1.05,
      fontWeight: 600,
      '@media (max-width:600px)': {
        fontSize: '2.5rem',
      },
    },
    h2: {
      ...appleHeadline,
      fontSize: '3rem',
      lineHeight: 1.08,
      '@media (max-width:600px)': {
        fontSize: '2.25rem',
      },
    },
    h3: {
      ...appleHeadline,
      fontSize: '2.25rem',
      lineHeight: 1.1,
      letterSpacing: '-0.015em',
    },
    h4: {
      ...appleHeadline,
      fontSize: '1.75rem',
      lineHeight: 1.15,
      letterSpacing: '-0.015em',
    },
    h5: {
      ...appleHeadline,
      fontSize: '1.35rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h6: {
      ...appleHeadline,
      fontSize: '1.1875rem',
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '1.125rem',
      lineHeight: 1.47,
      fontWeight: 400,
      color: '#6e6e73',
    },
    subtitle2: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '1rem',
      lineHeight: 1.43,
      fontWeight: 400,
      color: '#6e6e73',
    },
    body1: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '1.125rem',
      lineHeight: 1.47,
      fontWeight: 400,
      color: '#1d1d1f',
    },
    body2: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '1rem',
      lineHeight: 1.43,
      fontWeight: 400,
      color: '#6e6e73',
    },
    button: {
      fontFamily: APPLE_FONT_STACK,
      textTransform: 'none',
      fontWeight: 400,
      fontSize: '1.0625rem',
      letterSpacing: 'normal',
    },
    caption: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '0.875rem',
      lineHeight: 1.33,
      fontWeight: 400,
      color: '#6e6e73',
    },
    overline: {
      fontFamily: APPLE_FONT_STACK,
      fontSize: '0.8125rem',
      lineHeight: 1.33,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        body: {
          fontFamily: APPLE_FONT_STACK,
          fontSize: '18px',
          lineHeight: 1.47,
          color: '#1d1d1f',
          backgroundColor: '#ffffff',
        },
        '#root': {
          fontFamily: APPLE_FONT_STACK,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'p',
          subtitle2: 'p',
          body1: 'p',
          body2: 'p',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: APPLE_FONT_STACK,
          borderRadius: 980,
          padding: '6px 14px',
          fontWeight: 400,
          fontSize: '1.0625rem',
          minHeight: 'auto',
          textTransform: 'none',
          letterSpacing: 'normal',
          boxShadow: 'none',
        },
        sizeMedium: {
          fontSize: '1rem',
          padding: '6px 14px',
        },
        sizeLarge: {
          fontSize: '1.0625rem',
          padding: '8px 18px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: 1,
          '&:hover': {
            borderWidth: 1,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontFamily: APPLE_FONT_STACK,
          },
          '& .MuiInputLabel-root': {
            fontFamily: APPLE_FONT_STACK,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          fontFamily: APPLE_FONT_STACK,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          fontFamily: APPLE_FONT_STACK,
          borderRadius: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: APPLE_FONT_STACK,
          borderRadius: 16,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: APPLE_FONT_STACK,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: APPLE_FONT_STACK,
          fontWeight: 400,
          fontSize: '1rem',
        },
        secondary: {
          fontFamily: APPLE_FONT_STACK,
          fontSize: '0.9375rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: APPLE_FONT_STACK,
          fontSize: '1rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
        },
        input: {
          fontSize: '1.0625rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
        },
      },
    },
  },
});

export default theme;
